/**
 * MultiTradeTable — HTML table replacement for AG Grid in MultiTradeModal.
 *
 * Renders an editable table with controlled inputs for all trade fields.
 * Supports three modes: Normal, With Allocations, With Splits.
 */

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Trash2, ChevronDown, Scissors, Plus, Undo2, X } from 'lucide-react';
import { GroupingsConfig, EntityMapping } from '../../types/groupings';
import { TradingEntity } from '../../services/api/clientService';
import { TradeAllocationSet } from '../../types/tradeImageCapture';
import { AllocationColumnDef } from './MultiTradeModal';
import { getCurrency1Options, getValidCurrency2Options } from '../../types/currency';
import { parseAmountInput } from '../../utils/amountFormatter';
import FilterableSelect from './FilterableSelect';

// --- Row type (same as in MultiTradeModal) ---

interface MultiTradeRow {
  rowIndex: number;
  selected: boolean;
  tradeType: string;
  direction: string;
  amount: number | null;
  contraAmount: number | null;
  currency1: string;
  currency2: string;
  price: number | null;
  spotPrice: number | null;
  forwardPoints: number | null;
  tradeDate: string;
  valueDate: string;
  maturity: string;
  maturityDays: number | null;
  maturityDate: string;
  paymentDate: string;
  settlementType: string;
  settlementCurrency: string;
  counterparty: string;
  ourParty: string;
  fixingReference: string;
  grouping1: string;
  grouping2: string;
  grouping3: string;
  hedgeObject: string;
  isForwardStarting: boolean;
  spotFixingReference: string;
  notes: string;
  confidence: Record<string, number>;
  priceManuallyEdited: boolean;
  validationErrors: string[];
  // Split mode fields
  rowType: 'trade' | 'parent' | 'split';
  groupId?: string;
  splitPercentage?: number;
}

type GridMode = 'normal' | 'allocations';

// --- Column definition type ---

export interface TableColumnDef {
  field: string;
  headerKey: string;
  headerLabel?: string;
  width: number;
  type: 'text' | 'numeric' | 'date' | 'dropdown' | 'readonly';
  options?: string[];
  alwaysShow?: boolean;
  showCondition?: 'forwardStarting' | 'forwardStartingOnly' | 'compensacionOnly' | 'grouping1' | 'grouping2' | 'grouping3';
  editable?: boolean | ((row: MultiTradeRow) => boolean);
  pinned?: 'left' | 'right';
  decimals?: number;
  forwardStartingDisplay?: (row: MultiTradeRow, t: any) => string;
}

// --- Defaultable fields (dropdown columns that support per-user defaults) ---

const DEFAULTABLE_FIELDS = new Set([
  'tradeType', 'direction', 'currency1', 'currency2',
  'ourParty', 'counterparty',
  'settlementType', 'settlementCurrency', 'fixingReference',
]);

// --- Props ---

interface MultiTradeTableProps {
  trades: MultiTradeRow[];
  gridMode: GridMode;
  totalsRows: Record<string, any>[];
  allocations: TradeAllocationSet[];
  allocationColumns: AllocationColumnDef[];
  isForwardStartingExtraction: boolean;
  groupingsConfig: GroupingsConfig | null;
  tradingEntities: TradingEntity[];
  counterpartyNames: string[];
  ourPartyNames: string[];
  showValidation: boolean;
  columnDefaults: Record<string, string>;
  onColumnDefaultChange: (field: string, value: string) => void;
  onCellChange: (rowIndex: number, field: string, value: any) => void;
  onAllocCellChange: (rowIndex: number, allocIdx: number, value: number | null) => void;
  onAllocCopyDown: (rowIndex: number, allocIdx: number) => void;
  onRemoveAllocColumn: (columnIndex: number) => void;
  onAllocColumnUpdate: (columnIndex: number, hedgeObject: string, notes: string) => void;
  onCopyFieldDown: (rowIndex: number, field: string) => void;
  onCopyDateOffset: (rowIndex: number, field: string, offsetDays: number) => void;
  onCopyRow: (rowIndex: number, e: React.MouseEvent) => void;
  onDeleteRow: (rowIndex: number) => void;
  onAddRow: () => void;
  onSplitRow: (rowIndex: number) => void;
  onUnsplitRow: (rowIndex: number) => void;
  onAddSplitChild: (groupId: string) => void;
  onToggleSelect: (rowIndex: number) => void;
  onToggleSelectAll: () => void;
  onPasteValues: (field: string, rowIndex: number, text: string) => void;
  isSubmitting: boolean;
}

// --- Numeric formatting helper ---

function formatNumeric(val: number | null, decimals?: number): string {
  if (val == null) return '';
  if (decimals != null) {
    return Number(val).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }
  return Number(val).toLocaleString();
}

// --- Confidence class helper ---

function getConfidenceClass(field: string, row: MultiTradeRow): string {
  const score = row.confidence[field];
  if (score === undefined) return '';
  if (score >= 0.9) return '';
  if (score >= 0.6) return 'mt-cell-confidence-medium';
  return 'mt-cell-confidence-low';
}

// --- Row readiness check (only show validation when row has substantive data) ---

const READINESS_FIELDS = ['tradeType', 'direction', 'amount', 'currency1', 'currency2', 'price', 'tradeDate', 'counterparty', 'ourParty', 'settlementType'];

export function isRowReadyForValidation(row: MultiTradeRow): boolean {
  // Extracted rows (have confidence data) always show validation
  if (Object.keys(row.confidence).length > 0) return true;
  // Manual-entry rows: only show validation when 3+ mandatory fields are filled
  let filled = 0;
  for (const field of READINESS_FIELDS) {
    const val = (row as any)[field];
    if (val != null && val !== '') filled++;
    if (filled >= 3) return true;
  }
  return false;
}

// --- NumericCell component (handles focus/blur formatting) ---

const NumericCell: React.FC<{
  value: number | null;
  decimals?: number;
  readOnly?: boolean;
  readOnlyText?: string;
  parser?: (raw: string) => number | null;
  onChange: (val: number | null) => void;
  onCellFocus?: () => void;
  className?: string;
}> = React.memo(({ value, decimals, readOnly, readOnlyText, parser, onChange, onCellFocus, className }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  if (readOnly) {
    return <span className={`mt-cell-readonly ${className || ''}`}>{readOnlyText ?? formatNumeric(value, decimals)}</span>;
  }

  const parse = parser || parseAmountInput;

  const handleFocus = () => {
    setEditing(true);
    setEditValue(value != null ? String(value) : '');
    onCellFocus?.();
  };

  const handleBlur = () => {
    setEditing(false);
    const parsed = parse(editValue);
    if (parsed !== value) {
      onChange(parsed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <input
      type="text"
      className={`mt-cell-input mt-cell-numeric ${className || ''}`}
      value={editing ? editValue : formatNumeric(value, decimals)}
      onChange={(e) => setEditValue(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
});

// --- Display labels for dropdown values ---

export const OPTION_LABELS: Record<string, string> = {
  // Trade type
  FORWARD: 'Forward',
  FORWARD_STARTING: 'Forward (Starting)',
  SPOT: 'Spot',
  // Direction
  BUY: 'Buy',
  SELL: 'Sell',
  // Settlement type
  COMPENSACION: 'Compensación',
  ENTREGA_FISICA: 'Entrega Física',
  // Fixing reference
  USD_OBS: 'USD Obs',
  UF: 'UF',
  BLOOMBERG_11AM: 'Bloomberg 11 AM',
  'N/A': 'N/A',
};

function getOptionLabel(value: string): string {
  return OPTION_LABELS[value] || value;
}

// --- Static column definitions ---

export const BASE_COLUMNS: TableColumnDef[] = [
  { field: '_rowNum', headerKey: '#', width: 45, type: 'readonly', alwaysShow: true, pinned: 'left' },
  { field: 'tradeType', headerKey: 'product', width: 150, type: 'dropdown', alwaysShow: true, pinned: 'left', options: ['FORWARD', 'FORWARD_STARTING'] },
  { field: 'ourParty', headerKey: 'ourParty', width: 150, type: 'dropdown', alwaysShow: true, pinned: 'left' },
  { field: 'counterparty', headerKey: 'counterparty', width: 150, type: 'dropdown', alwaysShow: true, pinned: 'left' },
  { field: 'direction', headerKey: 'direction', width: 85, type: 'dropdown', alwaysShow: true, pinned: 'left', options: ['BUY', 'SELL'] },
  { field: 'amount', headerKey: 'amount', width: 120, type: 'numeric', alwaysShow: true, pinned: 'left' },
  { field: 'splitPercentage', headerKey: 'splitPercentage', width: 70, type: 'numeric', decimals: 2 },
  { field: 'currency1', headerKey: 'currency1', width: 85, type: 'dropdown', alwaysShow: true },
  { field: 'currency2', headerKey: 'currency2', width: 85, type: 'dropdown', alwaysShow: true },
  { field: 'spotPrice', headerKey: 'spotPrice', width: 110, type: 'numeric', decimals: 2, showCondition: 'forwardStarting',
    editable: (row) => !row.isForwardStarting,
    forwardStartingDisplay: (row) => {
      const ref = row.spotFixingReference;
      if (ref === 'USD_OBS') return 'USD Obs';
      if (ref === 'BLOOMBERG_11AM') return 'Bloomberg 11 AM';
      if (ref === 'UF') return 'UF';
      return ref || '';
    },
  },
  { field: 'forwardPoints', headerKey: 'forwardPoints', width: 100, type: 'numeric', decimals: 2, showCondition: 'forwardStarting' },
  { field: 'price', headerKey: 'price', width: 110, type: 'numeric', decimals: 2, alwaysShow: true,
    editable: (row) => !row.isForwardStarting,
    forwardStartingDisplay: (_row, t) => t('tradeBooking.forwardStarting.toBeFixed'),
  },
  { field: 'contraAmount', headerKey: 'contraAmount', width: 120, type: 'numeric',
    editable: (row) => !row.isForwardStarting,
    forwardStartingDisplay: (_row, t) => t('tradeBooking.forwardStarting.toBeFixed'),
  },
  { field: 'tradeDate', headerKey: 'tradeDate', width: 130, type: 'date', alwaysShow: true },
  { field: 'valueDate', headerKey: 'valueDate', width: 130, type: 'date', showCondition: 'forwardStartingOnly' },
  { field: 'maturity', headerKey: 'maturity', width: 90, type: 'text', alwaysShow: true },
  { field: 'maturityDate', headerKey: 'maturityDate', width: 130, type: 'date', alwaysShow: true },
  { field: 'paymentDate', headerKey: 'paymentDate', width: 130, type: 'date' },
  { field: 'settlementType', headerKey: 'settlementType', width: 130, type: 'dropdown', alwaysShow: true, options: ['COMPENSACION', 'ENTREGA_FISICA'] },
  { field: 'settlementCurrency', headerKey: 'settlementCurrency', width: 80, type: 'text', showCondition: 'compensacionOnly' },
  { field: 'fixingReference', headerKey: 'fixingReference', width: 120, type: 'dropdown', showCondition: 'compensacionOnly', options: ['USD_OBS', 'UF', 'BLOOMBERG_11AM'] },
  { field: 'grouping1', headerKey: 'grouping1', width: 120, type: 'dropdown', showCondition: 'grouping1' },
  { field: 'grouping2', headerKey: 'grouping2', width: 120, type: 'dropdown', showCondition: 'grouping2' },
  { field: 'grouping3', headerKey: 'grouping3', width: 120, type: 'dropdown', showCondition: 'grouping3' },
  { field: 'hedgeObject', headerKey: 'hedgeObject', width: 120, type: 'text' },
  { field: 'notes', headerKey: 'notes', width: 150, type: 'text' },
];

// Fields that are hidden by default and toggled via "+" button in the header
const OPTIONAL_TOGGLE_FIELDS = ['hedgeObject', 'notes'];

// --- Component ---

const MultiTradeTable: React.FC<MultiTradeTableProps> = ({
  trades,
  gridMode,
  totalsRows,
  allocations,
  allocationColumns,
  isForwardStartingExtraction,
  groupingsConfig,
  tradingEntities,
  counterpartyNames,
  ourPartyNames,
  showValidation,
  columnDefaults,
  onColumnDefaultChange,
  onCellChange,
  onAllocCellChange,
  onAllocCopyDown,
  onRemoveAllocColumn,
  onAllocColumnUpdate,
  onCopyFieldDown,
  onCopyDateOffset,
  onAddRow,
  onSplitRow,
  onUnsplitRow,
  onAddSplitChild,
  onCopyRow,
  onDeleteRow,
  onToggleSelect,
  onToggleSelectAll,
  onPasteValues,
  isSubmitting,
}) => {
  const { t } = useTranslation();

  // --- Active cell tracking (for paste) ---
  const [activeCell, setActiveCell] = useState<{ rowIndex: number; field: string } | null>(null);

  // --- Column default popover state ---
  const [defaultPopoverField, setDefaultPopoverField] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover on click outside
  useEffect(() => {
    if (!defaultPopoverField) return;
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setDefaultPopoverField(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [defaultPopoverField]);

  // --- Optional column toggle state (hedgeObject, notes) ---
  // Hidden by default; user expands via "+" button in the header stub
  const [expandedCols, setExpandedCols] = useState<Set<string>>(new Set());
  const toggleOptionalCol = useCallback((field: string) => {
    setExpandedCols(prev => {
      const next = new Set(prev);
      if (next.has(field)) { next.delete(field); } else { next.add(field); }
      return next;
    });
  }, []);

  // --- Allocation column notes popover state ---
  const [allocColPopover, setAllocColPopover] = useState<{
    columnIndex: number;
    hedgeObject: string;
    notes: string;
  } | null>(null);
  const allocColPopoverRef = useRef<HTMLDivElement>(null);

  // Close alloc column popover on click outside
  useEffect(() => {
    if (!allocColPopover) return;
    const handleClick = (e: MouseEvent) => {
      if (allocColPopoverRef.current && !allocColPopoverRef.current.contains(e.target as Node)) {
        setAllocColPopover(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [allocColPopover]);

  // --- Resolve entity mapping for a row's ourParty ---
  const getEntityMapping = useCallback((ourPartyName: string): EntityMapping | null => {
    if (!groupingsConfig || !ourPartyName) return null;
    const entity = tradingEntities.find(e => e.name === ourPartyName);
    if (!entity) return null;
    return groupingsConfig.entityMappings.find(m => m.entityId === entity.id) || null;
  }, [groupingsConfig, tradingEntities]);

  // --- Get filtered grouping values for a row ---
  const getGroupingOptions = useCallback((groupingNumber: 1 | 2 | 3, row: MultiTradeRow): string[] => {
    if (!groupingsConfig) return [];
    const mapping = getEntityMapping(row.ourParty);
    if (!mapping) return [];

    if (groupingNumber === 1) {
      return groupingsConfig.grouping1Values
        .filter(v => v.isActive && mapping.grouping1ValueIds.includes(v.id))
        .map(v => v.name);
    }

    if (groupingNumber === 2) {
      if (groupingsConfig.grouping2Mode === 'subgrouping') {
        const selectedG1 = groupingsConfig.grouping1Values.find(v => v.name === row.grouping1);
        if (!selectedG1) return [];
        const allowedIds = mapping.g1ToG2Mappings[selectedG1.id] || [];
        return groupingsConfig.grouping2Values
          .filter(v => v.isActive && allowedIds.includes(v.id))
          .map(v => v.name);
      }
      return groupingsConfig.grouping2Values
        .filter(v => v.isActive && mapping.grouping2ValueIds.includes(v.id))
        .map(v => v.name);
    }

    // groupingNumber === 3
    if (groupingsConfig.grouping3Mode === 'subgrouping') {
      const selectedG2 = groupingsConfig.grouping2Values.find(v => v.name === row.grouping2);
      if (!selectedG2) return [];
      const allowedIds = mapping.g2ToG3Mappings[selectedG2.id] || [];
      return groupingsConfig.grouping3Values
        .filter(v => v.isActive && allowedIds.includes(v.id))
        .map(v => v.name);
    }
    return groupingsConfig.grouping3Values
      .filter(v => v.isActive && mapping.grouping3ValueIds.includes(v.id))
      .map(v => v.name);
  }, [groupingsConfig, getEntityMapping]);

  // --- Resolve dropdown options per column (some depend on row data) ---
  const getOptions = useCallback((col: TableColumnDef, row?: MultiTradeRow): string[] => {
    if (col.field === 'ourParty') return ourPartyNames;
    if (col.field === 'counterparty') return counterpartyNames;
    if (col.field === 'currency1') {
      const tradeType = row?.tradeType || 'FORWARD';
      return getCurrency1Options(tradeType).map(c => c.code);
    }
    if (col.field === 'currency2') {
      return getValidCurrency2Options(row?.currency1 || null).map(c => c.code);
    }
    if ((col.field === 'grouping1' || col.field === 'grouping2' || col.field === 'grouping3') && row) {
      // For split rows, resolve options using parent's ourParty (and parent grouping values for cascade)
      const resolveRow = row.rowType === 'split'
        ? (() => {
            const parent = trades.find(t => t.rowType === 'parent' && t.groupId === row.groupId);
            return parent ? { ...parent, grouping1: row.grouping1, grouping2: row.grouping2, grouping3: row.grouping3 } as MultiTradeRow : row;
          })()
        : row;
      if (col.field === 'grouping1') return getGroupingOptions(1, resolveRow);
      if (col.field === 'grouping2') return getGroupingOptions(2, resolveRow);
      return getGroupingOptions(3, resolveRow);
    }
    return col.options || [];
  }, [ourPartyNames, counterpartyNames, getGroupingOptions, trades]);

  // --- Get options for column default popover (no row context) ---
  const getDefaultOptions = useCallback((field: string): string[] => {
    if (field === 'tradeType') return ['FORWARD', 'FORWARD_STARTING'];
    if (field === 'direction') return ['BUY', 'SELL'];
    if (field === 'currency1') return getCurrency1Options('FORWARD').map(c => c.code);
    if (field === 'currency2') return getValidCurrency2Options(null).map(c => c.code);
    if (field === 'ourParty') return ourPartyNames;
    if (field === 'counterparty') return counterpartyNames;
    if (field === 'settlementType') return ['COMPENSACION', 'ENTREGA_FISICA'];
    if (field === 'settlementCurrency') return ['CLP', 'USD', 'EUR'];
    if (field === 'fixingReference') return ['USD_OBS', 'UF', 'BLOOMBERG_11AM'];
    return [];
  }, [ourPartyNames, counterpartyNames]);

  // --- Resolve header label ---
  const getHeaderLabel = useCallback((col: TableColumnDef): string => {
    if (col.field === '_rowNum') return '#';
    if (col.field === 'grouping1') return groupingsConfig?.grouping1Label || t(`multiTrade.columns.${col.headerKey}`);
    if (col.field === 'grouping2') return groupingsConfig?.grouping2Label || t(`multiTrade.columns.${col.headerKey}`);
    if (col.field === 'grouping3') return groupingsConfig?.grouping3Label || t(`multiTrade.columns.${col.headerKey}`);
    return t(`multiTrade.columns.${col.headerKey}`);
  }, [t, groupingsConfig]);

  // --- Determine visible columns ---
  const visibleColumns = useMemo(() => {
    const hasData = (field: string): boolean =>
      trades.some(r => {
        const v = (r as any)[field];
        return v != null && v !== '';
      });

    return BASE_COLUMNS.filter(col => {
      // splitPercentage only when split rows exist
      if (col.field === 'splitPercentage') return trades.some(t => t.rowType === 'split');
      if (col.alwaysShow) return true;
      if (col.showCondition === 'forwardStarting') {
        return hasData(col.field) || isForwardStartingExtraction || trades.some(t => t.tradeType === 'FORWARD');
      }
      if (col.showCondition === 'forwardStartingOnly') {
        return isForwardStartingExtraction || trades.some(t => t.isForwardStarting);
      }
      if (col.showCondition === 'compensacionOnly') {
        return trades.some(t => t.settlementType === 'COMPENSACION');
      }
      if (col.showCondition === 'grouping1') {
        return hasData(col.field) || !!groupingsConfig?.grouping1Label;
      }
      if (col.showCondition === 'grouping2') {
        return hasData(col.field) || !!groupingsConfig?.grouping2Label;
      }
      if (col.showCondition === 'grouping3') {
        return hasData(col.field) || !!groupingsConfig?.grouping3Label;
      }
      // Optional toggle columns: show if user expanded them OR if they already have data
      if (OPTIONAL_TOGGLE_FIELDS.includes(col.field)) {
        return expandedCols.has(col.field) || hasData(col.field);
      }
      return hasData(col.field);
    });
  }, [trades, isForwardStartingExtraction, groupingsConfig, gridMode, expandedCols]);

  // Optional toggle fields not currently visible — rendered as "+" stub headers
  const hiddenOptionalCols = OPTIONAL_TOGGLE_FIELDS.filter(
    f => !visibleColumns.some(c => c.field === f)
  );

  // --- Pinned column offsets (cumulative left positions) ---
  const pinnedInfo = useMemo(() => {
    const offsets = new Map<string, number>();
    let cumulative = 0;
    let lastPinnedField = '';
    for (const col of visibleColumns) {
      if (col.pinned === 'left') {
        offsets.set(col.field, cumulative);
        cumulative += col.width;
        lastPinnedField = col.field;
      }
    }
    return { offsets, lastPinnedField };
  }, [visibleColumns]);

  // Helper: get pinned class + inline left style for a column
  const getPinnedProps = useCallback((field: string): { className: string; style: React.CSSProperties } => {
    const left = pinnedInfo.offsets.get(field);
    if (left === undefined) return { className: '', style: {} };
    const isLast = field === pinnedInfo.lastPinnedField;
    return {
      className: `mt-cell-pinned-left${isLast ? ' mt-cell-pinned-last' : ''}`,
      style: { left },
    };
  }, [pinnedInfo]);

  // --- Allocation columns ---
  const hasAllocations = gridMode === 'allocations' && allocationColumns.length > 0;

  // Build flat allocation column headers from allocationColumns
  const allocHeaders = useMemo((): { label: string; index: number }[] => {
    if (gridMode !== 'allocations') return [];
    return allocationColumns.map((col, idx) => {
      // Build label from entity + groupings
      const parts = [col.entityName, col.grouping1, col.grouping2, col.grouping3].filter(Boolean);
      return { label: parts.join(' / ') || `Alloc ${idx + 1}`, index: idx };
    });
  }, [gridMode, allocationColumns]);

  // --- Augmented trades (merge alloc amounts) ---
  const augmentedTrades = useMemo(() => {
    if (allocations.length === 0) return trades;
    return trades.map(trade => {
      const augmented: Record<string, any> = { ...trade };
      const tradeAlloc = allocations.find(a => a.tradeRowIndex === trade.rowIndex);
      if (tradeAlloc) {
        let total = 0;
        tradeAlloc.allocations.forEach((entry, idx) => {
          const amt = entry.amount ?? null;
          augmented[`alloc_${idx}`] = amt;
          if (amt != null) total += amt;
        });
        augmented.alloc_total = total;
      }
      return augmented as MultiTradeRow;
    });
  }, [trades, allocations]);

  // --- Price mismatch helper ---
  const getPriceMismatch = useCallback((row: MultiTradeRow): boolean => {
    if (row.spotPrice == null || row.forwardPoints == null || row.price == null) return false;
    const calc = row.spotPrice + row.forwardPoints;
    return Math.abs(calc - row.price) > 0.0001;
  }, []);

  // --- Check if all selected ---
  const allSelected = trades.length > 0 && trades.every(r => r.selected);

  // --- Render a cell ---
  const renderCell = (col: TableColumnDef, row: MultiTradeRow, rowDisplayIdx: number, isLastRow: boolean) => {
    const val = (row as any)[col.field];
    const confidenceClass = getConfidenceClass(col.field, row);
    const isEditable = col.editable === undefined ? true : (typeof col.editable === 'function' ? col.editable(row) : col.editable);
    const showCopyDown = !isLastRow && col.type !== 'readonly' && col.field !== '_rowNum';
    const pinned = getPinnedProps(col.field);

    // Row number column
    if (col.field === '_rowNum') {
      const label = rowLabels.get(row.rowIndex) ?? String(rowDisplayIdx + 1);
      return <td key={col.field} className={`mt-cell ${pinned.className} mt-cell-row-num`} style={{ width: col.width, ...pinned.style }}>{label}</td>;
    }

    // splitPercentage is only editable on split child rows, not on parent or normal trade rows
    if (col.field === 'splitPercentage' && row.rowType !== 'split') {
      return <td key={col.field} className={`mt-cell ${pinned.className}`} style={{ width: col.width, minWidth: col.width, ...pinned.style }}></td>;
    }

    // Split rows: only certain fields are editable, rest show parent value as read-only
    const SPLIT_EDITABLE_FIELDS = ['amount', 'splitPercentage', 'grouping1', 'grouping2', 'grouping3', 'hedgeObject', 'notes'];
    if (row.rowType === 'split' && !SPLIT_EDITABLE_FIELDS.includes(col.field)) {
      // Show inherited parent value as muted read-only text, transformed to user-facing labels
      const parentRow = augmentedTrades.find(t => t.rowType === 'parent' && t.groupId === row.groupId);
      let inheritedVal = parentRow ? (parentRow as any)[col.field] : '';
      // For tradeType, respect isForwardStarting on the parent (same as the dropdown render path at line 663)
      if (col.field === 'tradeType' && parentRow?.isForwardStarting) inheritedVal = 'FORWARD_STARTING';
      // For Forward Starting columns (spotPrice → fixing ref label, price/contraAmount → "Por Fijar"),
      // delegate to the column's forwardStartingDisplay using the parent row's data.
      if (parentRow?.isForwardStarting && col.forwardStartingDisplay) {
        const displayText = col.forwardStartingDisplay(parentRow, t);
        return (
          <td key={col.field} className={`mt-cell mt-cell-split-inherited ${pinned.className}`} style={{ width: col.width, minWidth: col.width, ...pinned.style }}>
            <span className="mt-cell-readonly">{displayText}</span>
          </td>
        );
      }
      let display = '';
      if (inheritedVal != null && inheritedVal !== '') {
        if (col.type === 'dropdown') {
          display = getOptionLabel(String(inheritedVal));
        } else if (col.type === 'numeric' && typeof inheritedVal === 'number') {
          display = inheritedVal.toLocaleString(undefined, { maximumFractionDigits: col.decimals ?? 2 });
        } else if (col.type === 'date' && typeof inheritedVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(inheritedVal)) {
          // Convert YYYY-MM-DD to DD/MM/YYYY for display
          const [y, m, d] = inheritedVal.split('-');
          display = `${d}/${m}/${y}`;
        } else {
          display = String(inheritedVal);
        }
      }
      return (
        <td key={col.field} className={`mt-cell mt-cell-split-inherited ${pinned.className}`} style={{ width: col.width, minWidth: col.width, ...pinned.style }}>
          <span className="mt-cell-readonly">{display}</span>
        </td>
      );
    }

    // Helper: wrap cell content + copy-down button in a flex column div
    const wrapWithCopyDown = (content: React.ReactNode, canCopy: boolean = true) => (
      <div className="mt-cell-col">
        {content}
        {showCopyDown && canCopy && (
          col.field === 'maturityDate' ? (
            <div className="mt-copy-down-row">
              <button
                className="mt-copy-down-btn"
                onClick={(e) => { e.stopPropagation(); onCopyFieldDown(row.rowIndex, col.field); }}
                tabIndex={-1}
              >
                <ChevronDown size={10} />
              </button>
              <button
                className="mt-copy-down-btn mt-copy-down-offset"
                onClick={(e) => { e.stopPropagation(); onCopyDateOffset(row.rowIndex, col.field, 7); }}
                tabIndex={-1}
              >
                +7
              </button>
              <button
                className="mt-copy-down-btn mt-copy-down-offset"
                onClick={(e) => { e.stopPropagation(); onCopyDateOffset(row.rowIndex, col.field, 30); }}
                tabIndex={-1}
              >
                +30
              </button>
            </div>
          ) : (
            <button
              className="mt-copy-down-btn"
              onClick={(e) => { e.stopPropagation(); onCopyFieldDown(row.rowIndex, col.field); }}
              tabIndex={-1}
            >
              <ChevronDown size={10} />
            </button>
          )
        )}
      </div>
    );

    // Special: Forward Starting — spotPrice becomes spotFixingReference dropdown
    if (row.isForwardStarting && col.field === 'spotPrice') {
      const fixRefOpts = ['USD_OBS', 'UF', 'BLOOMBERG_11AM'];
      const fixRefVal = row.spotFixingReference || 'USD_OBS';
      return (
        <td key={col.field} className={`mt-cell ${confidenceClass} ${pinned.className}`} style={{ width: col.width, ...pinned.style }}>
          {wrapWithCopyDown(
            <select
              className="mt-cell-select"
              value={fixRefVal}
              onChange={(e) => onCellChange(row.rowIndex, 'spotFixingReference', e.target.value)}
            >
              {fixRefOpts.map(o => <option key={o} value={o}>{getOptionLabel(o)}</option>)}
            </select>
          )}
        </td>
      );
    }

    // Special: Forward Starting display overrides (price, contraAmount → "To Be Fixed")
    if (row.isForwardStarting && col.forwardStartingDisplay && !isEditable) {
      const displayText = col.forwardStartingDisplay(row, t);
      return (
        <td key={col.field} className={`mt-cell mt-cell-readonly ${confidenceClass} ${pinned.className}`} style={{ width: col.width, ...pinned.style }} title={displayText}>
          <span className="mt-cell-readonly">{displayText}</span>
        </td>
      );
    }

    // Price columns: highlight all 3 when spot + fwdPts ≠ price
    let extraClass = '';
    const PRICE_FIELDS = ['spotPrice', 'forwardPoints', 'price'];
    if (PRICE_FIELDS.includes(col.field) && !row.isForwardStarting && getPriceMismatch(row)) {
      extraClass = 'mt-cell-price-mismatch';
    }

    switch (col.type) {
      case 'dropdown': {
        const opts = getOptions(col, row);
        // For tradeType, show FORWARD_STARTING when isForwardStarting is true
        const displayVal = col.field === 'tradeType' && row.isForwardStarting ? 'FORWARD_STARTING' : (val || '');
        // Use FilterableSelect for grouping columns (long option lists)
        const isGroupingField = col.field === 'grouping1' || col.field === 'grouping2' || col.field === 'grouping3';
        if (isGroupingField) {
          return (
            <td key={col.field} className={`mt-cell ${confidenceClass} ${extraClass} ${pinned.className}`} style={{ width: col.width, ...pinned.style }}>
              {wrapWithCopyDown(
                <FilterableSelect
                  className="mt-cell-filterable"
                  value={String(displayVal)}
                  options={opts}
                  onChange={(v) => onCellChange(row.rowIndex, col.field, v)}
                  placeholder={`— ${getHeaderLabel(col)} —`}
                />
              )}
            </td>
          );
        }
        return (
          <td key={col.field} className={`mt-cell ${confidenceClass} ${extraClass} ${pinned.className}`} style={{ width: col.width, ...pinned.style }}>
            {wrapWithCopyDown(
              <select
                className={`mt-cell-select ${!displayVal ? 'mt-cell-select-empty' : ''}`}
                value={displayVal}
                onChange={(e) => onCellChange(row.rowIndex, col.field, e.target.value)}
              >
                <option value="">— {getHeaderLabel(col)} —</option>
                {opts.map(o => <option key={o} value={o}>{getOptionLabel(o)}</option>)}
              </select>
            )}
          </td>
        );
      }

      case 'numeric': {
        return (
          <td key={col.field} className={`mt-cell ${confidenceClass} ${extraClass} ${pinned.className}`} style={{ width: col.width, ...pinned.style }}>
            {wrapWithCopyDown(
              <NumericCell
                value={val}
                decimals={col.decimals}
                readOnly={!isEditable}
                readOnlyText={row.isForwardStarting && col.forwardStartingDisplay ? col.forwardStartingDisplay(row, t) : undefined}
                onChange={(v) => onCellChange(row.rowIndex, col.field, v)}
                onCellFocus={() => setActiveCell({ rowIndex: row.rowIndex, field: col.field })}
              />,
              isEditable
            )}
          </td>
        );
      }

      case 'date': {
        return (
          <td key={col.field} className={`mt-cell ${confidenceClass} ${pinned.className}`} style={{ width: col.width, ...pinned.style }}>
            {wrapWithCopyDown(
              <input
                type="date"
                className="mt-cell-input mt-cell-date"
                value={val || ''}
                onChange={(e) => onCellChange(row.rowIndex, col.field, e.target.value)}
              />
            )}
          </td>
        );
      }

      case 'text': {
        return (
          <td key={col.field} className={`mt-cell ${confidenceClass} ${pinned.className}`} style={{ width: col.width, ...pinned.style }}>
            {wrapWithCopyDown(
              <input
                type="text"
                className="mt-cell-input"
                value={val || ''}
                onChange={(e) => onCellChange(row.rowIndex, col.field, e.target.value)}
                onFocus={() => setActiveCell({ rowIndex: row.rowIndex, field: col.field })}
              />
            )}
          </td>
        );
      }

      case 'readonly':
      default:
        return (
          <td key={col.field} className={`mt-cell ${pinned.className}`} style={{ width: col.width, ...pinned.style }}>
            {val != null ? String(val) : ''}
          </td>
        );
    }
  };

  // --- Render actions cell (context-dependent buttons) ---
  const renderActionsCell = (row: MultiTradeRow) => {
    // Parent row: copy + unsplit + delete + checkbox
    if (row.rowType === 'parent') {
      return (
        <td className="mt-cell mt-cell-pinned-right mt-cell-actions">
          <button className="mt-row-action-btn mt-row-copy-btn" onClick={(e) => onCopyRow(row.rowIndex, e)} title={t('multiTrade.copyRow')}><Copy size={14} /></button>
          <button className="mt-row-action-btn mt-row-unsplit-btn" onClick={() => onUnsplitRow(row.rowIndex)} title={t('multiTrade.splits.unsplitRow')}><Undo2 size={14} /></button>
          <button className="mt-row-action-btn mt-row-delete-btn" onClick={() => onDeleteRow(row.rowIndex)} title={t('multiTrade.deleteRow')}><Trash2 size={14} /></button>
          <input type="checkbox" checked={row.selected} onChange={() => onToggleSelect(row.rowIndex)} />
        </td>
      );
    }
    // Split child row: delete only
    if (row.rowType === 'split') {
      return (
        <td className="mt-cell mt-cell-pinned-right mt-cell-actions">
          <button className="mt-row-action-btn mt-row-delete-btn" onClick={() => onDeleteRow(row.rowIndex)} title={t('multiTrade.deleteRow')}><Trash2 size={14} /></button>
        </td>
      );
    }
    // Normal trade row: copy + split (not in allocations mode) + delete + checkbox
    return (
      <td className="mt-cell mt-cell-pinned-right mt-cell-actions">
        <button className="mt-row-action-btn mt-row-copy-btn" onClick={(e) => onCopyRow(row.rowIndex, e)} title={t('multiTrade.copyRow')}><Copy size={14} /></button>
        {gridMode !== 'allocations' && (
          <button className="mt-row-action-btn mt-row-split-btn" onClick={() => onSplitRow(row.rowIndex)} title={t('multiTrade.splits.splitRow')}><Scissors size={14} /></button>
        )}
        <button className="mt-row-action-btn mt-row-delete-btn" onClick={() => onDeleteRow(row.rowIndex)} title={t('multiTrade.deleteRow')}><Trash2 size={14} /></button>
        <input type="checkbox" checked={row.selected} onChange={() => onToggleSelect(row.rowIndex)} />
      </td>
    );
  };

  // --- Render allocation cells for a row ---
  const renderAllocCells = (row: MultiTradeRow, isLastRow: boolean) => {
    if (gridMode !== 'allocations') return null;
    const augRow = row as Record<string, any>;

    return (
      <>
        {allocHeaders.map(ah => (
          <td key={`alloc_${ah.index}`} className="mt-cell mt-cell-alloc">
            <div className="mt-cell-col">
              <NumericCell
                value={augRow[`alloc_${ah.index}`] ?? null}
                onChange={(v) => onAllocCellChange(row.rowIndex, ah.index, v)}
                onCellFocus={() => setActiveCell({ rowIndex: row.rowIndex, field: `alloc_${ah.index}` })}
                parser={(raw) => {
                  const trimmed = raw.trim();
                  if (trimmed.endsWith('%') && row.amount && row.amount > 0) {
                    const pct = parseFloat(trimmed.slice(0, -1).trim());
                    if (!isNaN(pct) && pct > 0 && pct <= 100) {
                      return Math.round((pct / 100) * row.amount * 100) / 100;
                    }
                    return null;
                  }
                  return parseAmountInput(raw);
                }}
              />
              {!isLastRow && (
                <button
                  className="mt-copy-down-btn"
                  onClick={(e) => { e.stopPropagation(); onAllocCopyDown(row.rowIndex, ah.index); }}
                  tabIndex={-1}
                >
                  <ChevronDown size={10} />
                </button>
              )}
            </div>
          </td>
        ))}
        {hasAllocations && (
          <td className={`mt-cell mt-cell-alloc-total ${augRow.alloc_total != null && row.amount != null && augRow.alloc_total > (row.amount + 1) ? 'mt-cell-alloc-mismatch' : ''}`}>
            <span>{augRow.alloc_total != null ? Number(augRow.alloc_total).toLocaleString() : ''}</span>
            {augRow.alloc_total != null && row.amount != null && augRow.alloc_total < (row.amount - 1) && (
              <span className="mt-alloc-remaining"> ({Number(row.amount - augRow.alloc_total).toLocaleString()})</span>
            )}
          </td>
        )}
      </>
    );
  };

  // --- Row class ---
  const getRowClass = (row: MultiTradeRow, displayIdx: number): string => {
    const classes = ['mt-row', displayIdx % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'];
    if (showValidation && row.validationErrors?.length > 0) classes.push('mt-row-error');
    if (!row.selected) classes.push('mt-row-deselected');
    if (row.rowType === 'parent') classes.push('mt-row-parent');
    if (row.rowType === 'split') classes.push('mt-row-split');
    return classes.join(' ');
  };

  // --- Compute split group info ---
  const splitGroups = useMemo(() => {
    const groups = new Map<string, { parent: MultiTradeRow; splits: MultiTradeRow[]; totalAmount: number; totalPercentage: number }>();
    if (!augmentedTrades.some(t => t.rowType === 'parent' || t.rowType === 'split')) return groups;
    for (const row of augmentedTrades) {
      if (row.groupId && row.rowType === 'parent') {
        groups.set(row.groupId, { parent: row, splits: [], totalAmount: 0, totalPercentage: 0 });
      }
    }
    for (const row of augmentedTrades) {
      if (row.groupId && row.rowType === 'split') {
        const group = groups.get(row.groupId);
        if (group) {
          group.splits.push(row);
          group.totalAmount += row.amount || 0;
          group.totalPercentage += row.splitPercentage || 0;
        }
      }
    }
    return groups;
  }, [augmentedTrades]);

  // --- Compute row labels (e.g. "3", "3.1", "3.2") ---
  const rowLabels = useMemo(() => {
    const labels = new Map<number, string>();
    let tradeNum = 0;
    for (const row of augmentedTrades) {
      if (row.rowType === 'trade' || row.rowType === 'parent') {
        tradeNum++;
        labels.set(row.rowIndex, String(tradeNum));
      } else if (row.rowType === 'split' && row.groupId) {
        const group = splitGroups.get(row.groupId);
        const parentLabel = labels.get(group?.parent.rowIndex ?? -1) || '?';
        const splitIdx = group ? group.splits.indexOf(row) + 1 : 0;
        // splitGroups may not be computed yet at this point, so fallback to counting
        if (splitIdx > 0) {
          labels.set(row.rowIndex, `${parentLabel}.${splitIdx}`);
        } else {
          // Fallback: count splits seen so far for this group
          let count = 0;
          for (const r of augmentedTrades) {
            if (r.rowType === 'split' && r.groupId === row.groupId) {
              count++;
              if (r.rowIndex === row.rowIndex) break;
            }
          }
          labels.set(row.rowIndex, `${parentLabel}.${count}`);
        }
      }
    }
    return labels;
  }, [augmentedTrades, splitGroups]);

  // --- Is row the last split in its group? ---
  const isLastSplitInGroup = (row: MultiTradeRow): boolean => {
    if (row.rowType !== 'split' || !row.groupId) return false;
    const group = splitGroups.get(row.groupId);
    if (!group || group.splits.length === 0) return false;
    return group.splits[group.splits.length - 1].rowIndex === row.rowIndex;
  };

  // --- Render group total row ---
  const renderGroupTotalRow = (groupId: string) => {
    const group = splitGroups.get(groupId);
    if (!group) return null;
    const parentAmount = group.parent.amount || 0;
    const balanced = parentAmount > 0 && Math.abs(group.totalAmount - parentAmount) < 1;
    const totalColCount = visibleColumns.length + (hasAllocations ? allocHeaders.length + 1 : 0) + hiddenOptionalCols.length + 1;

    return (
      <React.Fragment key={`group-total-${groupId}`}>
        <tr className={`mt-row mt-row-group-total ${balanced ? 'mt-group-balanced' : 'mt-group-unbalanced'}`}>
          {visibleColumns.map(col => {
            const p = getPinnedProps(col.field);
            if (col.field === '_rowNum') {
              return <td key={col.field} className={`mt-cell ${p.className}`} style={{ ...p.style }}></td>;
            }
            if (col.field === 'amount') {
              return (
                <td key={col.field} className={`mt-cell mt-cell-total-amount ${p.className}`} style={{ width: col.width, ...p.style }}>
                  {group.totalAmount.toLocaleString()} {balanced ? '✓' : `/ ${parentAmount.toLocaleString()}`}
                </td>
              );
            }
            if (col.field === 'splitPercentage') {
              return (
                <td key={col.field} className={`mt-cell mt-cell-total-amount ${p.className}`} style={{ width: col.width, ...p.style }}>
                  {Math.round(group.totalPercentage * 100) / 100}%
                </td>
              );
            }
            if (col.field === 'direction') {
              return <td key={col.field} className={`mt-cell mt-cell-total-label ${p.className}`} style={{ width: col.width, ...p.style }}>{t('multiTrade.splits.groupTotal')}</td>;
            }
            return <td key={col.field} className={`mt-cell ${p.className}`} style={{ width: col.width, ...p.style }}></td>;
          })}
          {/* Optional stub cells */}
          {hiddenOptionalCols.map(field => (
            <td key={`stub-${field}`} className="mt-cell mt-optional-stub-cell" style={{ width: 70 }} />
          ))}
          {/* Actions: add-split button */}
          <td className="mt-cell mt-cell-pinned-right mt-cell-actions">
            <button
              className="mt-row-action-btn mt-row-add-split-btn"
              onClick={() => onAddSplitChild(groupId)}
              title={t('multiTrade.splits.addSplit')}
            >
              <Plus size={14} />
            </button>
          </td>
        </tr>
      </React.Fragment>
    );
  };

  // --- Totals row ---
  const renderTotalsRow = (totalRow: Record<string, any>, idx: number) => (
    <tr key={`total-${idx}`} className="mt-row mt-row-total">
      {visibleColumns.map(col => {
        const p = getPinnedProps(col.field);
        if (col.field === '_rowNum') {
          return <td key={col.field} className={`mt-cell ${p.className}`} style={{ ...p.style }}></td>;
        }
        if (col.field === 'direction') {
          return <td key={col.field} className={`mt-cell mt-cell-total-label ${p.className}`} style={{ width: col.width, ...p.style }}>{totalRow.direction}</td>;
        }
        if (col.field === 'amount') {
          return <td key={col.field} className={`mt-cell mt-cell-total-amount ${p.className}`} style={{ width: col.width, ...p.style }}>{totalRow.amount != null ? Number(totalRow.amount).toLocaleString() : ''}</td>;
        }
        return <td key={col.field} className={`mt-cell ${p.className}`} style={{ width: col.width, ...p.style }}></td>;
      })}
      {/* Allocation totals */}
      {gridMode === 'allocations' && allocHeaders.map(ah => (
        <td key={`alloc_${ah.index}`} className="mt-cell mt-cell-alloc">
          {totalRow[`alloc_${ah.index}`] != null ? Number(totalRow[`alloc_${ah.index}`]).toLocaleString() : ''}
        </td>
      ))}
      {hasAllocations && (
        <td className="mt-cell mt-cell-alloc-total">
          {totalRow.alloc_total != null ? Number(totalRow.alloc_total).toLocaleString() : ''}
        </td>
      )}
      {/* Optional stub cells */}
      {hiddenOptionalCols.map(field => (
        <td key={`stub-${field}`} className="mt-cell mt-optional-stub-cell" style={{ width: 70 }} />
      ))}
      {/* Actions: empty */}
      <td className="mt-cell mt-cell-pinned-right mt-cell-actions"></td>
    </tr>
  );

  return (
    <div className="mt-table-wrapper" onPaste={(e) => {
      if (!activeCell) return;
      const PASTEABLE_FIELDS = ['spotPrice', 'forwardPoints', 'price', 'amount', 'maturity', 'hedgeObject', 'notes'];
      const isPasteable = PASTEABLE_FIELDS.includes(activeCell.field) || activeCell.field.startsWith('alloc_');
      if (!isPasteable) return;
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      if (text) onPasteValues(activeCell.field, activeCell.rowIndex, text);
    }}>
      <table className="mt-table">
        <thead>
          <tr>
            {visibleColumns.map(col => {
              const p = getPinnedProps(col.field);
              const isDefaultable = DEFAULTABLE_FIELDS.has(col.field);
              const hasDefault = isDefaultable && !!columnDefaults[col.field];
              const isPopoverOpen = defaultPopoverField === col.field;
              return (
                <th key={col.field} className={`mt-header ${p.className}`} style={{ width: col.width, minWidth: col.width, ...p.style, position: p.style.left !== undefined ? 'sticky' : 'relative' }}>
                  <div className="mt-header-content">
                    <span>{getHeaderLabel(col)}</span>
                    {isDefaultable && (
                      <button
                        className={`mt-header-default-btn${hasDefault ? ' mt-header-default-active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setDefaultPopoverField(isPopoverOpen ? null : col.field); }}
                        title={t('multiTrade.columnDefault.setDefault')}
                      >
                        ⚙️
                      </button>
                    )}
                    {/* Collapse button for optional toggle columns */}
                    {OPTIONAL_TOGGLE_FIELDS.includes(col.field) && expandedCols.has(col.field) && (
                      <button
                        className="mt-optional-collapse-btn"
                        onClick={(e) => { e.stopPropagation(); toggleOptionalCol(col.field); }}
                        title={t('common.hide', 'Ocultar')}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {isPopoverOpen && (
                    <div className="mt-default-popover" ref={popoverRef} onClick={(e) => e.stopPropagation()}>
                      <div className="mt-default-popover-title">{t('multiTrade.columnDefault.title')}</div>
                      <select
                        className="mt-default-popover-select"
                        value={columnDefaults[col.field] || ''}
                        onChange={(e) => {
                          onColumnDefaultChange(col.field, e.target.value);
                          setDefaultPopoverField(null);
                        }}
                      >
                        <option value="">{t('multiTrade.columnDefault.noDefault')}</option>
                        {getDefaultOptions(col.field).map(o => (
                          <option key={o} value={o}>{getOptionLabel(o)}</option>
                        ))}
                      </select>
                      {hasDefault && (
                        <button
                          className="mt-default-popover-clear"
                          onClick={() => {
                            onColumnDefaultChange(col.field, '');
                            setDefaultPopoverField(null);
                          }}
                        >
                          {t('multiTrade.columnDefault.clear')}
                        </button>
                      )}
                    </div>
                  )}
                </th>
              );
            })}
            {/* Allocation headers (stacked: entity on top, groupings below) */}
            {hasAllocations && allocationColumns.map((col, idx) => {
              const isPopoverOpen = allocColPopover?.columnIndex === idx;
              return (
                <th
                  key={`alloc_${idx}`}
                  className="mt-header mt-header-alloc"
                  style={{ width: 120, minWidth: 120, position: 'relative' }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setAllocColPopover({ columnIndex: idx, hedgeObject: col.hedgeObject, notes: col.notes });
                  }}
                >
                  <div className="mt-alloc-header-content">
                    <div className="mt-alloc-header-stacked">
                      <span className="mt-alloc-header-entity">{col.entityName}</span>
                      {(col.grouping1 || col.grouping2 || col.grouping3) && (
                        <span className="mt-alloc-header-groupings">
                          {[col.grouping1, col.grouping2, col.grouping3].filter(Boolean).join(' / ')}
                        </span>
                      )}
                      {/* Visual indicator: truncated hedge object / notes */}
                      {col.hedgeObject && (
                        <span className="mt-alloc-header-meta">
                          {t('multiTrade.allocations.columnHedgeObjectAbbr')}: {col.hedgeObject.length > 20 ? col.hedgeObject.slice(0, 20) + '…' : col.hedgeObject}
                        </span>
                      )}
                      {col.notes && (
                        <span className="mt-alloc-header-meta">
                          {t('multiTrade.allocations.columnNotesAbbr')}: {col.notes.length > 20 ? col.notes.slice(0, 20) + '…' : col.notes}
                        </span>
                      )}
                    </div>
                    <button
                      className="mt-alloc-header-remove"
                      onClick={() => onRemoveAllocColumn(idx)}
                      title={t('multiTrade.allocations.removeColumn')}
                    >
                      <X size={12} />
                    </button>
                  </div>
                  {/* Right-click popover: Allocation Notes */}
                  {isPopoverOpen && (
                    <div
                      className="mt-alloc-col-popover"
                      ref={allocColPopoverRef}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="mt-alloc-col-popover-title">{t('multiTrade.allocations.columnNotes')}</div>
                      <label className="mt-alloc-col-popover-label">{t('multiTrade.allocations.columnHedgeObject')}</label>
                      <input
                        type="text"
                        className="mt-alloc-col-popover-input"
                        value={allocColPopover.hedgeObject}
                        maxLength={50}
                        onChange={(e) => setAllocColPopover(prev => prev ? { ...prev, hedgeObject: e.target.value } : prev)}
                      />
                      <label className="mt-alloc-col-popover-label">{t('multiTrade.allocations.columnNotesLabel')}</label>
                      <input
                        type="text"
                        className="mt-alloc-col-popover-input"
                        value={allocColPopover.notes}
                        maxLength={200}
                        onChange={(e) => setAllocColPopover(prev => prev ? { ...prev, notes: e.target.value } : prev)}
                      />
                      <div className="mt-alloc-col-popover-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            onAllocColumnUpdate(allocColPopover.columnIndex, allocColPopover.hedgeObject, allocColPopover.notes);
                            setAllocColPopover(null);
                          }}
                        >
                          {t('multiTrade.allocations.columnNotesSave')}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setAllocColPopover(null)}
                        >
                          {t('multiTrade.allocations.columnNotesCancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </th>
              );
            })}
            {hasAllocations && (
              <th className="mt-header mt-header-alloc" style={{ width: 100, minWidth: 100 }}>
                {t('multiTrade.columns.allocTotal')}
              </th>
            )}
            {/* Optional column "+" stubs — appear at end when the column is hidden */}
            {hiddenOptionalCols.map(field => {
              const colDef = BASE_COLUMNS.find(c => c.field === field)!;
              return (
                <th key={`stub-${field}`} className="mt-header mt-optional-stub" style={{ width: 70, minWidth: 70 }}>
                  <button
                    className="mt-optional-expand-btn"
                    onClick={() => toggleOptionalCol(field)}
                    title={getHeaderLabel(colDef)}
                  >
                    <span className="mt-optional-stub-label">{getHeaderLabel(colDef)}</span>
                    <span className="mt-optional-stub-plus">+</span>
                  </button>
                </th>
              );
            })}
            {/* Actions header — spacers match row action buttons so checkbox aligns */}
            <th className="mt-header mt-cell-pinned-right mt-header-actions" style={{ width: 80, minWidth: 80 }}>
              <span className="mt-row-action-btn" style={{ visibility: 'hidden' }}><Copy size={14} /></span>
              <span className="mt-row-action-btn" style={{ visibility: 'hidden' }}><Trash2 size={14} /></span>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleSelectAll}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {augmentedTrades.map((row, displayIdx) => {
            const showErrors = showValidation && row.validationErrors.length > 0;
            const totalColCount = visibleColumns.length + (hasAllocations ? allocHeaders.length + 1 : 0) + hiddenOptionalCols.length + 1;
            const isLastRow = displayIdx === augmentedTrades.length - 1;
            const showGroupTotal = isLastSplitInGroup(row);
            return (
              <React.Fragment key={row.rowIndex}>
                <tr className={getRowClass(row, displayIdx)}>
                  {visibleColumns.map(col => renderCell(col, row, displayIdx, isLastRow))}
                  {renderAllocCells(row, isLastRow)}
                  {hiddenOptionalCols.map(field => (
                    <td key={`stub-${field}`} className="mt-cell mt-optional-stub-cell" style={{ width: 70 }} />
                  ))}
                  {renderActionsCell(row)}
                </tr>
                {showErrors && (
                  <tr className="mt-error-row">
                    <td colSpan={totalColCount} className="mt-error-row-content">
                      {row.validationErrors.join(' \u00b7 ')}
                    </td>
                  </tr>
                )}
                {showGroupTotal && row.groupId && renderGroupTotalRow(row.groupId)}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          {totalsRows.map((totalRow, idx) => renderTotalsRow(totalRow, idx))}
        </tfoot>
      </table>
      <div className="multi-trade-add-row-bar">
        <button
          className="btn btn-secondary btn-sm"
          onClick={onAddRow}
          disabled={isSubmitting}
        >
          {t('multiTrade.addEmptyRow')}
        </button>
      </div>
    </div>
  );
};

export default MultiTradeTable;
